// ---------------------------------------------------------------------------
// BIL-022 Phase 5 — Billing Records Retention CQRS Event Catalog
// (vocabulary tier)
// ---------------------------------------------------------------------------
// Story BIL-022: "Billing records support export/retention/erasure policy
// while preserving legal accounting records."
//
// Phase 1 LANDED 2026-05-14 (`packages/db/src/schema/billing-retention.ts`):
//   - `BILLING_RECORDS_CATEGORY` constant, closed
//     `BILLING_RECORDS_RETENTION_BASES`, `BILLING_RECORDS_RETENTION_DAYS`
//     map, `BILLING_RECORDS_TABLES` inventory, `resolveBillingRetentionBasis`
//     resolver, `getBillingRetentionDays` lookup.
//
// Phase 2 LANDED 2026-05-15 — `tenants.billing_jurisdiction` +
// `tax_residency_country` columns + migration 0112.
//
// Phase 5 (this file): catalogue the single CQRS event type the future
// Phase 3 erasure-handler (in
// `packages/platform/src/services/gdpr/erasure-handlers/billing-records-handler.ts`)
// will emit when a tenant erasure request triggers the Art. 17(3)(b)
// carve-out — i.e. when the erasure handler keeps billing rows beyond the
// generic erasure scope because the resolved retention basis requires it.
//
// Per BIL-022-DECISION-PACKET §"Audit log of accounting carve-out":
//   `billing.erasure_carve_out_applied@v1`
//
// Mirrors the EM-019 / EM-013 / EM-015 / MKT-016 / AG-027 Phase 5 catalog
// pattern.
//
// What this file is NOT:
//   - It does not emit events — that's the Phase 3 erasure handler.
//   - It does not change Phase 1 vocabulary or Phase 2 schema (additive).
//   - It does not declare a route — there is no HTTP route; the event is
//     emitted from the DSAR/erasure pipeline.
// ---------------------------------------------------------------------------

import {
  BILLING_RECORDS_RETENTION_BASES,
  type BillingRecordsRetentionBasis,
} from "./billing-retention";

/**
 * Closed catalog of BIL-022 CQRS event types. Versioned with `@v1` suffix
 * per Governance Rule #7 — future shape changes ship `@v2` alongside; never
 * mutate in place. One business fact per event (Governance Rule #6).
 *
 *   - `erasure_carve_out_applied` — the erasure handler kept N billing
 *     rows under the Art. 17(3)(b) accounting carve-out for the resolved
 *     retention basis (EU-10y / US-7y / RO-5y / FR-6y / INFINITE).
 *
 * Sized as a tuple (not single string) so future BIL-022 lifecycle events
 * (`retention_basis_changed@v1`, `retention_expiry_pruned@v1`) can extend
 * without a schema rename.
 */
export const BIL_022_BILLING_RETENTION_EVENT_TYPES = [
  "billing.erasure_carve_out_applied@v1",
] as const;

export type Bil022BillingRetentionEventType =
  (typeof BIL_022_BILLING_RETENTION_EVENT_TYPES)[number];

/**
 * Mirror of the Phase 1 retention-basis vocabulary, re-exported here so
 * downstream consumers (DLQ, audit-runner, materializer) can import the
 * event-catalog and the vocabulary from one place. Drift guard test pins
 * these to `BILLING_RECORDS_RETENTION_BASES` verbatim.
 */
export const BIL_022_RETENTION_BASIS_VOCAB = [
  ...BILLING_RECORDS_RETENTION_BASES,
] as const;

export type Bil022RetentionBasisVocab =
  (typeof BIL_022_RETENTION_BASIS_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Runtime narrowing guard. DLQ consumers and the audit-runner use this to
 * reject unknown event types at the boundary instead of silently fanning
 * them downstream.
 */
export function isBil022BillingRetentionEventType(
  value: unknown,
): value is Bil022BillingRetentionEventType {
  return (
    typeof value === "string" &&
    (BIL_022_BILLING_RETENTION_EVENT_TYPES as readonly string[]).includes(
      value,
    )
  );
}

/**
 * Runtime narrowing guard for the retention-basis vocabulary. The Phase 3
 * erasure handler MUST validate before emitting so a drifted caller cannot
 * inject a free-form basis string into the audit event log.
 */
export function isBil022RetentionBasisVocab(
  value: unknown,
): value is Bil022RetentionBasisVocab {
  return (
    typeof value === "string" &&
    (BIL_022_RETENTION_BASIS_VOCAB as readonly string[]).includes(value)
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload contract                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Per-table carve-out tally. The Phase 3 handler emits one row per table in
 * `BILLING_RECORDS_TABLES` whose generic erasure was suppressed under the
 * carve-out. Auditors replay this verbatim to answer "which rows were
 * redacted for which tenant under which legal basis".
 *
 * Mirrors `BILLING_RECORDS_TABLES` (`invoices`, `payments`,
 * `commission_ledger`, `stripe_customers`) — drift guarded by tests.
 */
export interface Bil022CarveOutTableSummary {
  /** Table name from the Phase 1 inventory (`invoices`, `payments`, ...). */
  table: string;
  /** Row count retained under the carve-out (NOT erased). */
  rowsRetained: number;
  /** Row count erased after the retention window matured (carve-out lifted). */
  rowsErased: number;
}

export interface Bil022ErasureCarveOutAppliedPayload {
  /** Tenant scope for fan-out + isolation. */
  tenantId: string;
  /**
   * DSAR / erasure request identifier the carve-out was applied to. Idempotency
   * key — the handler MUST emit at most once per `(tenantId, dsarRequestId)`.
   */
  dsarRequestId: string;
  /**
   * Resolved retention basis from `resolveBillingRetentionBasis(jurisdiction)`.
   * MUST be one of `BIL_022_RETENTION_BASIS_VOCAB`.
   */
  retentionBasis: Bil022RetentionBasisVocab;
  /**
   * Jurisdiction value read off `tenants.billing_jurisdiction` (Phase 2). NULL
   * is legal — the resolver falls back to EU-10y on NULL/empty.
   */
  jurisdiction: string | null;
  /**
   * Retention window in days (`getBillingRetentionDays(basis)`). NULL when
   * basis is `INFINITE` (the `billing_events` immutable CQRS log row).
   */
  retentionDays: number | null;
  /** Per-table carve-out tally. Ordered by `BILLING_RECORDS_TABLES`. */
  tables: ReadonlyArray<Bil022CarveOutTableSummary>;
  /** ISO-8601 — when the erasure handler completed the carve-out pass. */
  appliedAt: string;
}

/**
 * Discriminated union mapping each event type to its payload. Consumers
 * (analytics materializer, DLQ, audit-runner) can `switch (event.type)` and
 * pull the right payload shape without any-casting.
 */
export type Bil022BillingRetentionEvent = {
  type: "billing.erasure_carve_out_applied@v1";
  payload: Bil022ErasureCarveOutAppliedPayload;
};
